// The signed-out homepage. Its only solid action is Sign in; the example answer is
// static and marked as a sample. See Design > Names and definitions, "The Console".
// PublicFrame is the top bar and footer that Sign in, Policies and Welcome share.

import type { ComponentChildren } from "preact";
import { Button, CodeBlock, Icon, SampleMark, StatusBadge, Wordmark } from "../../../../ui/src/index.ts";
import { href } from "../router.ts";
import { useStore } from "../store.ts";
import { NotConnected } from "../ui.tsx";

const PublicFooter = () => (
  <footer class="cs-public__footer">
    <div class="cs-public__inner cs-public__footer-row">
      <span>By using this site, you agree to our <a href={href("policies")}>Policies</a> and <a href="https://warmhub.ai/tos/">Terms of Service</a>.</span>
      <span class="cs-public__footer-links">
        <a href="https://warmhub.ai/privacy/">Privacy</a>
        <a href="https://warmhub.ai/contact/">Contact</a>
        <a href="https://warmhub.ai">warmhub.ai</a>
      </span>
    </div>
  </footer>
);

/**
 * The frame of every page outside the signed-in shell: a slim top bar whose brand
 * links home, then the page. `links` adds the public links and Sign in to the bar;
 * `footer` adds the footer.
 */
export function PublicFrame({ home = "", links = false, footer = false, children }: { home?: string; links?: boolean; footer?: boolean; children: ComponentChildren }) {
  return (
    <div class="cs-public">
      <header class="cs-public__bar">
        <div class="cs-public__inner cs-public__bar-row">
          <a class="cs-public__brand" href={href(home)}><Wordmark product="Console" /></a>
          {links && (
            <nav class="cs-public__nav" aria-label="Public">
              <Button variant="ghost" href="https://docs.warmhub.ai">Documentation</Button>
              <Button variant="ghost" href="https://warmhub.ai/for-curators">For curators</Button>
              <Button href={href("sign-in")}>Sign in</Button>
            </nav>
          )}
        </div>
      </header>
      <main class="cs-public__main">{children}</main>
      {footer && <PublicFooter />}
    </div>
  );
}

const PROPS: [string, string][] = [
  ["Trusted", "Every fact traces back to its evidence."],
  ["Curated", "Someone accountable chooses the facts."],
  ["Versioned", "Changes are tracked: what changed, when, and what depended on it."],
  ["Timely", "Context is delivered at the moment of use."],
];

const STEPS: [string, string][] = [
  ["Ask, with a bid", "Send a question and the most you'll pay for an answer, in US dollars per million answers."],
  ["Hubs that know the subject answer", "WarmHub finds the hubs whose subject the question is about. A hub that can't answer says so, and it's free."],
  ["Pay for answers, reject the wrong ones", "Only answers are charged, at the hub's ask. Reject one and you're refunded at once."],
];

export function Landing() {
  // The example question comes from the content files; the answer stays a placeholder because it isn't live.
  const question = useStore((s) => s.content?.useCases[0]?.text ?? "[EXAMPLE QUESTION]");
  return (
    <PublicFrame links footer>
      <div class="cs-public__inner">
        <section class="cs-hero">
          <div class="cs-hero__text">
            <h1 class="cs-display cs-display--hero">The <strong>right facts</strong>,<br />not all the facts.</h1>
            <p class="cs-lede">Ask a question in plain words. Hubs run by accountable curators answer from their WarmHub data, and every answer traces back to its evidence.</p>
            <div class="cs-actions">
              <Button variant="solid" href={href("sign-in")}>Sign in to try it<Icon name="arrow" size={16} /></Button>
              <NotConnected label="Copy the prompt">Copy the prompt</NotConnected>
            </div>
          </div>
          <figure class="cs-example">
            <figcaption class="cs-example__head">
              <span>Example question</span>
              <SampleMark title="Not live: this example is a placeholder, not a real answer" />
              <span class="ui-sr-only">(not live)</span>
            </figcaption>
            <p class="cs-example__q">{question}</p>
            <div class="cs-example__answer">
              <div class="cs-example__from"><StatusBadge tone="success">Answer</StatusBadge><span class="mono">from [hub] · [intent]</span></div>
              <p class="cs-example__a mono">[ANSWER]</p>
            </div>
            <p class="cs-example__meta">
              <span>Paid <strong class="mono">[PRICE]</strong></span>
              <span>Took <strong class="mono">[MS] ms</strong></span>
              <span>Sources <strong class="mono">[N]</strong>, each pinned to a version</span>
            </p>
          </figure>
        </section>

        <section class="cs-props">
          {PROPS.map(([h, t]) => <div key={h}><h3>{h}.</h3><p>{t}</p></div>)}
        </section>

        <section class="cs-how">
          <h2 class="cs-h2">How it works</h2>
          <ol class="cs-how__steps">
            {STEPS.map(([h, t], i) => (
              <li key={h}><span class="cs-how__num">{String(i + 1).padStart(2, "0")}</span><strong>{h}</strong><span>{t}</span></li>
            ))}
          </ol>
        </section>

        <section class="cs-prompt" id="prompt">
          <div>
            <h2 class="cs-display cs-display--sm">Don't take our word for it. <strong>Ask your agent.</strong></h2>
            <p>Let your agent decide if we're worth your time.</p>
          </div>
          <div class="cs-prompt__code">
            <CodeBlock label="Agent prompt" code="[Agent prompt]" />
            <NotConnected label="Copy the prompt">Copy the prompt</NotConnected>
          </div>
        </section>
      </div>
    </PublicFrame>
  );
}
